import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import { Search, Home } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface Suggestion {
  label: string;
  type: "neighborhood" | "city" | "postal" | "address";
  /** For address suggestions - links directly to property */
  id?: string;
  /** Additional context like city for addresses */
  context?: string;
}

interface SearchInputWithSuggestionsProps {
  value: string;
  onChange: (value: string) => void;
  onSuggestionSelect?: (suggestion: Suggestion) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  suggestions: Suggestion[];
  placeholder?: string;
  className?: string;
  inputClassName?: string;
}

export function SearchInputWithSuggestions({
  value,
  onChange,
  onSuggestionSelect,
  onKeyDown,
  suggestions,
  placeholder = "Search properties...",
  className,
  inputClassName,
}: SearchInputWithSuggestionsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [justSelected, setJustSelected] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter and sort suggestions: communities/neighborhoods first, then addresses
  const filteredSuggestions = (() => {
    if (value.length < 2) return [];
    const lowerValue = value.toLowerCase();
    const matched = suggestions.filter(s =>
      s.label.toLowerCase().includes(lowerValue) &&
      s.label.toLowerCase() !== lowerValue
    );
    // Sort: neighborhood/city/postal first, addresses after
    const typeOrder: Record<string, number> = { city: 0, neighborhood: 1, postal: 2, address: 3 };
    matched.sort((a, b) => (typeOrder[a.type] ?? 9) - (typeOrder[b.type] ?? 9));
    return matched.slice(0, 10);
  })();

  const showSuggestions = isOpen && filteredSuggestions.length > 0 && !justSelected;

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setJustSelected(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) {
      onKeyDown?.(e);
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredSuggestions.length - 1
        );
        break;
      case "Enter":
        if (highlightedIndex >= 0) {
          e.preventDefault();
          handleSelect(filteredSuggestions[highlightedIndex]);
        } else {
          onKeyDown?.(e);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
      default:
        onKeyDown?.(e);
    }
  };

  const handleSelect = (suggestion: Suggestion) => {
    setJustSelected(true);
    setIsOpen(false);
    onChange(suggestion.label);
    onSuggestionSelect?.(suggestion);
    inputRef.current?.blur();
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setJustSelected(false);
    onChange(e.target.value);
    setIsOpen(true);
  };

  const handleFocus = () => {
    if (!justSelected) {
      setIsOpen(true);
    }
  };

  // Highlight matching text in suggestion
  const highlightMatch = (text: string, query: string) => {
    if (!query || query.length < 2) return text;
    
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const matchIndex = lowerText.indexOf(lowerQuery);
    
    if (matchIndex === -1) return text;
    
    const before = text.slice(0, matchIndex);
    const match = text.slice(matchIndex, matchIndex + query.length);
    const after = text.slice(matchIndex + query.length);
    
    return (
      <>
        {before}
        <span className="font-semibold text-primary">{match}</span>
        {after}
      </>
    );
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
      <Input
        ref={inputRef}
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        className={cn("pl-9", inputClassName)}
        autoComplete="off"
      />
      
      {showSuggestions && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg z-50 overflow-hidden max-h-80 overflow-y-auto">
          {(() => {
            let lastType: string | null = null;
            return filteredSuggestions.map((suggestion, index) => {
              const showHeader = suggestion.type !== lastType;
              lastType = suggestion.type;
              return (
                <div key={`${suggestion.type}-${suggestion.id || suggestion.label}`}>
                  {showHeader && (
                    <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider bg-muted/50 border-b">
                      {suggestion.type === 'address' ? 'Listings' : suggestion.type === 'neighborhood' ? 'Communities' : suggestion.type === 'postal' ? 'Postal Codes' : 'Cities'}
                    </div>
                  )}
                  <button
                    type="button"
                    aria-label={`Select ${suggestion.label}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(suggestion);
                    }}
                    className={cn(
                      "w-full py-2 text-left text-sm flex items-center gap-2 hover:bg-accent transition-colors pl-9 pr-3",
                      highlightedIndex === index && "bg-accent"
                    )}
                  >
                    {suggestion.type === 'address' && (
                      <Home className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <span className="block truncate">{highlightMatch(suggestion.label, value)}</span>
                      {suggestion.context && (
                        <span className="block text-xs text-muted-foreground truncate">{suggestion.context}</span>
                      )}
                    </div>
                  </button>
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
}
